import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser, authKeys } from '@/api/auth';
import { AUTH_LOGOUT_EVENT } from '@/api/client';
import { tokenStorage, type AuthMethod } from '@/lib/token-storage';
import { decodeJwtPayload } from '@/lib/jwt';
import { setSentryUser } from '@/lib/sentry';

interface AuthContextValue {
  user: import('@/auth/types').User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Persist a freshly issued session, then refetch the current user. */
  setSession: (tokens: { access_token: string; refresh_token?: string }, method?: AuthMethod) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Reactive mirror of "is there a token?" — flipping this re-enables the
  // current-user query immediately after login (a bare localStorage read isn't
  // reactive and would leave the query disabled until a full reload).
  const [hasToken, setHasToken] = useState(() => Boolean(tokenStorage.access));

  const { data: user, isLoading, isError } = useCurrentUser(hasToken);

  const setSession = useCallback(
    (
      tokens: { access_token: string; refresh_token?: string },
      method: AuthMethod = 'sso',
    ): boolean => {
      const { access_token, refresh_token } = tokens;
      if (!access_token) return false;
      const claims = decodeJwtPayload(access_token) as { exp?: number; sub?: string };
      tokenStorage.set({
        access: access_token,
        refresh: refresh_token,
        expSeconds: claims.exp,
        uuid: claims.sub,
        authMethod: method,
      });
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
      setHasToken(true);
      return true;
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    setSentryUser(null);
    tokenStorage.clear();
    queryClient.removeQueries({ queryKey: authKeys.currentUser });
    setHasToken(false);
    window.location.assign('/auth/login');
  }, [queryClient]);

  // React to forced logouts raised by the API layer (refresh failure / 401).
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(AUTH_LOGOUT_EVENT, handler);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
  }, [logout]);

  // Attach the signed-in user to Sentry once the current-user query resolves.
  useEffect(() => {
    if (user?.uuid) setSentryUser(user);
  }, [user?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<AuthContextValue>(
    () => ({
      user: user ?? null,
      isLoading: hasToken && isLoading,
      isAuthenticated: Boolean(user) && !isError,
      setSession,
      logout,
    }),
    [user, isLoading, isError, hasToken, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
